import { useState, useCallback, useEffect, ComponentPropsWithRef } from 'react';
import { EmblaCarouselType } from 'embla-carousel'; // Adjust the import path as necessary
import emblaStyles from './embla.module.scss';

/**
 * Custom hook to handle Embla Carousel dot button navigation
 *
 * @param emblaApi - The Embla Carousel API
 * @param onButtonClick - Optional function to handle dot button clicks
 * @returns Object with selected index, scroll snaps, and click handler
 *
 * @example
 * ```ts
 * const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(
 *  emblaApi,
 *  onNavButtonClick
 * );
 * ```
 */
const useDotButton = (
    emblaApi: EmblaCarouselType | undefined,
    onButtonClick?: (emblaApi: EmblaCarouselType) => void,
): {
    selectedIndex: number;
    scrollSnaps: number[];
    onDotButtonClick: (index: number) => void;
} => {
    // State for selected index and scroll snaps
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

    // Function to handle dot button clicks and scroll to the selected index
    const onDotButtonClick = useCallback(
        (index: number) => {
            if (!emblaApi) return;
            emblaApi.scrollTo(index);
            if (onButtonClick) onButtonClick(emblaApi);
        },
        [emblaApi, onButtonClick],
    );

    // Function to set the scroll snaps
    const onInit = useCallback((emblaApi: EmblaCarouselType) => {
        setScrollSnaps(emblaApi.scrollSnapList());
    }, []);

    // Function to set the selected index
    const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, []);

    // useEffect to setup the scroll snaps, selected index, and their event listeners
    useEffect(() => {
        if (!emblaApi) return;

        onInit(emblaApi);
        onSelect(emblaApi);
        emblaApi
            .on('reInit', onInit)
            .on('reInit', onSelect)
            .on('select', onSelect);
    }, [emblaApi, onInit, onSelect]);

    // Return the selected index, scroll snaps, and click handler
    return {
        selectedIndex,
        scrollSnaps,
        onDotButtonClick,
    };
};

/**
 * Custom Embla Carousel dot button component
 *
 * @remarks This component is used to create dot buttons for Embla Carousel navigation.
 * The button gets it's functional props like onClick from the parent component.
 *
 * @param props - Button props
 * @returns The dot button element
 */
const CarouselDot = (props: ComponentPropsWithRef<'button'>) => {
    // Destructure the children and rest props
    const { children, ...restProps } = props;

    // Return the button element with the rest props
    return <button {...restProps} />;
};

/**
 * Embla Carousel dot navigation component
 * 
 * @param props - The component props object
 * @param props.emblaApi - The Embla Carousel API for the carousel instance
 * @param props.onNavButtonClick - function to handle carousel navigation button clicks 
 * @returns The CarouselDots component for use in Embla Carousels
 * 
 * @example
 * ```tsx
 * <CarouselDots
 *  emblaApi={emblaApi}
 *  onNavButtonClick={onNavButtonClick}
 * />
 * ```
 */
const CarouselDots = ({
    emblaApi,
    onNavButtonClick,
}: {
    emblaApi: EmblaCarouselType | undefined;
    onNavButtonClick: (emblaApi: EmblaCarouselType) => void;
}) => {
    // Get and destructure dot navigation hook params
    const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(
        emblaApi,
        onNavButtonClick,
    );

    return (
        <div className={emblaStyles.embla__dots}>
            {/* Map through scroll snaps to create dot buttons */}
            {scrollSnaps.map((_, index) => {
                const isSelected = index === selectedIndex;
                return (
                    <CarouselDot
                        key={index}
                        onClick={() => onDotButtonClick(index)}
                        className={emblaStyles.embla__dot.concat(
                            isSelected ? ` ${emblaStyles.embla__dot__selected}` : '',
                        )}
                        aria-label={ isSelected ? `Slide ${index + 1} is selected` : `Go to slide ${index + 1}` }
                        aria-current={isSelected ? 'true' : 'false'}
                    />
                );
            })}
        </div>
    );
};

export default CarouselDots;